import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import { HYDRATE } from 'next-redux-wrapper';
import { GlobalAppState } from '@model/state';

export enum ModalActions {
  SLICE_NAME = '@MODAL',
  SHOW_MODAL = '@MODAL/showModal',
  SHOW_MODAL_BASKET_ERROR = '@MODAL/handleBasketError',
  SHOW_MODAL_INVALID_SEAT_SELECTION = '@MODAL/handleInvalidSeatSelection',
  SHOW_MODAL_DEAL_FINDER_TOKEN_EXPIRED = '@MODAL/handleDealFinderTokenExpired',
  SHOW_MODAL_LOCATION_CHANGE = '@MODAL/handleLocationChange',
  CLOSE_MODAL = '@MODAL/closeModal',
  CLOSE_VIEW_ROOMS_MODAL = '@MODAL/closeViewRoomsModal'
}

export enum ModalType {
  NONE = 'NONE',
  APIS_FORM_ERROR = 'APIS_FORM_ERROR',
  BASKET_ERROR = 'BASKET_ERROR',
  BEST_PRICE_GUARANTEE_MODAL = 'BEST_PRICE_GUARANTEE_MODAL',
  CONFIRMATION_MODAL = 'CONFIRMATION_MODAL',
  CONTACT_FORM_ERROR = 'CONTACT_FORM_ERROR',
  DEAL_FINDER_TOKEN_EXPIRED = 'DEAL_FINDER_TOKEN_EXPIRED',
  EARLY_FLIGHTS_MODAL = 'EARLY_FLIGHTS_MODAL',
  EPC_MODAL = 'EPC_MODAL',
  EXIT_SEAT_SELECTION = 'EXIT_SEAT_SELECTION',
  FORM_SUBMIT_SUCCESS = 'FORM_SUBMIT_SUCCESS',
  FORM_SUBMIT_ERROR = 'FORM_SUBMIT_ERROR',
  GENERIC_ERROR = 'GENERIC_ERROR',
  GUEST_PAGE_FORM_ERROR = 'GUEST_PAGE_FORM_ERROR',
  INVALID_SEAT_SELECTION = 'INVALID_SEAT_SELECTION',
  MMB_BOOKING_DOMAIN_ERROR = 'MMB_BOOKING_DOMAIN_ERROR',
  MMB_LOGIN_FORM_ERROR = 'MMB_LOGIN_FORM_ERROR',
  PAYMENT_PAGE_FORM_ERROR = 'PAYMENT_PAGE_FORM_ERROR',
  PAYWALL_BANNER_MODAL = 'PAYWALL_BANNER_MODAL',
  PRIVATE_TOUR_ENQUIRY = 'PRIVATE_TOUR_ENQUIRY',
  PROMO_BANNER_MODAL = 'PROMO_BANNER_MODAL',
  RYANAIR_CONFIRM_FLIGHTS_MODAL = 'RYANAIR_CONFIRM_FLIGHTS_MODAL',
  SEARCH_ERROR = 'SEARCH_ERROR',
  SEARCH_GUEST_FORM_ERROR = 'SEARCH_GUEST_FORM_ERROR',
  SUPPLIER_TERMS_AND_CONDITIONS = 'SUPPLIER_TERMS_AND_CONDITIONS',
  TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
  TOURS_ERROR = 'TOURS_ERROR',
  TRANSFER_LUGGAGE_MODAL = 'TRANSFER_LUGGAGE_MODAL',
  UNMATCHED_CHILD_AGE = 'UNMATCHED_CHILD_AGE',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  VIEW_ROOM_TYPES_MODAL = 'VIEW_ROOM_TYPES_MODAL'
}

interface ModalHistory {
  hasClosedViewRoomTypesModal: boolean;
}

export interface ModalState {
  modalType: ModalType;
  modalData: any;
  modalHistory: ModalHistory;
}

export const INITIAL_MODAL_STATE: ModalState = {
  modalType: ModalType.NONE,
  modalData: {},
  modalHistory: {
    hasClosedViewRoomTypesModal: false
  }
};

export const getShowModalPayload = (modalTypeProps: ModalType, modalDataProps?: any) => {
  return { modalType: modalTypeProps, modalData: modalDataProps };
};

/**
 * Thunk to handle basket error scenarios
 * Replaces handleShowBasketErrorModal saga
 */
export const handleBasketError = createAsyncThunk(
  ModalActions.SHOW_MODAL_BASKET_ERROR,
  async (errorCode: string, { dispatch }) => {
    dispatch(
      showModal({
        modalType: ModalType.BASKET_ERROR,
        modalData: { code: errorCode }
      })
    );
    return errorCode;
  }
);

/**
 * Thunk to handle invalid seat selection
 * Replaces handleShowInvalidSeatSelectionModal saga
 */
export const handleInvalidSeatSelection = createAsyncThunk(
  ModalActions.SHOW_MODAL_INVALID_SEAT_SELECTION,
  async (_, { dispatch }) => {
    dispatch(showModal(getShowModalPayload(ModalType.INVALID_SEAT_SELECTION)));
  }
);

/**
 * Thunk to handle deal finder token expiration
 * Replaces handleShowDealFinderTokenExpiredModal saga
 */
export const handleDealFinderTokenExpired = createAsyncThunk(
  ModalActions.SHOW_MODAL_DEAL_FINDER_TOKEN_EXPIRED,
  async (_, { dispatch }) => {
    dispatch(showModal(getShowModalPayload(ModalType.DEAL_FINDER_TOKEN_EXPIRED)));
  }
);

/**
 * Thunk to handle location changes and auto-close non-persistent modals
 * Replaces handleOnLocationChange saga
 */
export const handleLocationChange = createAsyncThunk(
  ModalActions.SHOW_MODAL_LOCATION_CHANGE,
  async (_, { getState, dispatch }) => {
    const state = getState() as GlobalAppState;
    const modalType = state.app.modal.modalType;
    const modalData = state.app.modal.modalData;

    if (modalType !== ModalType.NONE && !modalData.persist) {
      dispatch(closeModal());
    }
  }
);

const modalSlice = createSlice({
  name: ModalActions.SLICE_NAME,
  initialState: INITIAL_MODAL_STATE,
  reducers: {
    showModal: (state, action: PayloadAction<{ modalType: ModalType; modalData?: any }>) => {
      state.modalType = action.payload.modalType;
      state.modalData = action.payload.modalData || {};
    },
    closeModal: () => INITIAL_MODAL_STATE,
    closeViewRoomsModal: (state) => {
      state.modalHistory.hasClosedViewRoomTypesModal = true;
      state.modalType = ModalType.NONE;
      state.modalData = {};
    }
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action: any) => {
      if (isEqual(state, INITIAL_MODAL_STATE)) {
        return action.payload?.app?.modal || state;
      }
      return state;
    });
  }
});

export const { showModal, closeModal, closeViewRoomsModal } = modalSlice.actions;
export const modalReducer = modalSlice.reducer;
